import { type Row, type Table as TableType, flexRender } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { getColumnPinningClassName, getColumnPinningStyle } from './getColumnPinningClassNames';
import { SkeletonRow } from './SkeletonRow';

/**
 * テーブル構造コンポーネント
 * テーブルの構造的レンダリング（ヘッダー、データ行、スケルトン行）に関する責任を持つ
 */
interface TableStructureProps<TData> {
  /** TanStack Tableインスタンス */
  table: TableType<TData>;
  /** テーブル要素のCSSクラス */
  className?: string;
  /** 行クリックイベントハンドラ */
  onRowClick: (event: React.MouseEvent<HTMLTableRowElement>, rowData: Row<TData>) => void;
  /** ローディング状態かどうか */
  isLoading?: boolean;
  /** スケルトン行数 */
  skeletonRowCount?: number;
  /** 行ホバー時に Hover を表示するかどうか */
  isHoverable?: boolean;
  /** 行ホバー時に Cursor Pointer を表示するかどうか */
  isClickable: boolean;
  /** 行ごとのクリック可否 */
  isRowClickable?: (row: Row<TData>) => boolean;
}
export function TableStructure<TData>({
  table,
  className,
  onRowClick,
  isLoading = false,
  skeletonRowCount = 0,
  isHoverable = true,
  isClickable,
  isRowClickable
}: TableStructureProps<TData>) {
  return <Table className={cn('w-full', className)}>
      {/* スティッキーヘッダー（スクロール時も常に表示） */}
      <TableHeader className={cn('sticky top-0 z-20 shadow-xs bg-neutral-fill', table.options.meta?.getHeaderAreaClassName?.(table))}>
        {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => {
          const thStyle = header.column.columnDef.meta?.thStyle ?? {};
          return <TableHead key={header.id} colSpan={header.colSpan} style={{
            ...getColumnPinningStyle(header.column),
            ...thStyle,
            ...(!thStyle.width && {
              width: 'fit-content'
            })
          }} className={cn('first:pl-6', getColumnPinningClassName(header.column), header.column.getIsPinned() && 'bg-neutral-fill', table.options.meta?.getHeaderCellClassName?.(header.column, table), header.column.columnDef.meta?.getHeaderCellClassName?.(header))} role="columnheader" aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : undefined}>
                  {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>;
        })}
          </TableRow>)}
      </TableHeader>

      <TableBody>
        {/* スケルトンローディング表示 */}
        {isLoading ? Array.from({
        length: skeletonRowCount
      }, (_, index) => <SkeletonRow key={`skeleton-row-${index}`} table={table} rowIndex={index} />) : /* 通常のデータ行を表示 */
      table.getRowModel().rows.map(row => {
        const rowIsClickable = isClickable && (isRowClickable?.(row) ?? true);
        const rowIsHoverable = isHoverable && (isRowClickable?.(row) ?? true);
        return <TableRow key={row.id} data-state={row.getIsSelected() ? 'selected' : undefined} role="row" className={cn('group', 'bg-neutral-fill', rowIsClickable && 'cursor-pointer', table.options.meta?.getRowClassName?.(row, table))} onClick={event => onRowClick(event, row)} onKeyDown={event => {
          if (!rowIsClickable) return;
          if (event.currentTarget !== event.target) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
          }
        }} tabIndex={rowIsClickable ? 0 : undefined}>
                  {(() => {
            const cells = row.getVisibleCells();
            const nodes: ReactNode[] = [];
            for (let i = 0; i < cells.length;) {
              const cell = cells[i];
              const colSpan = cell.column.columnDef.meta?.getColSpan?.(cell) ?? 1;
              const extraCellProps = table.options.meta?.getCellProps?.(cell, table);
              const {
                style: extraCellStyle,
                ...restExtraCellProps
              } = extraCellProps ?? {};
              nodes.push(<TableCell key={cell.id} colSpan={colSpan > 1 ? colSpan : undefined} {...restExtraCellProps} className={cn('first:pl-4', 'relative z-0 bg-inherit', rowIsHoverable && ['before:absolute before:inset-0 before:z-[-1] before:bg-primary-muted-fill', 'before:opacity-0 before:transition-opacity before:pointer-events-none', 'group-hover:before:opacity-100'], rowIsClickable && 'cursor-pointer', getColumnPinningClassName(cell.column), table.options.meta?.getCellClassName?.(cell, table), cell.column.columnDef.meta?.getCellClassName?.(cell))} style={{
                ...getColumnPinningStyle(cell.column),
                ...(cell.column.columnDef.meta?.tdStyle ?? {}),
                ...extraCellStyle
              }} role="cell">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>);
              i += Math.max(colSpan, 1);
            }
            return nodes;
          })()}
                </TableRow>;
      })}
      </TableBody>
    </Table>;
}